import React from "react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../axios/AxiosInstance";
import {
  Home,
  Briefcase,
  UserCheck,
  FileText,
  Library,
  Repeat,
  Clock,
  Building,
  Calendar,
  Bell,
  Activity,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [nombreFacturesImpayees, setNombreFacturesImpayees] = useState(0);
  const [contratsASigner, setContratsASigner] = useState(0);
  const [rhASaisir, setRhASaisir] = useState(0);
  const [prolongationsATraiter, setProlongationsATraiter] = useState(0);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Charger les données des nouvelles métriques
      const [facturesRes, contratsRes, rhRes, prolongationsRes] = await Promise.all([
        AxiosInstance.get("facture/client/countNonSoldee"),
        AxiosInstance.get("pld-contract/count-unsigned"), // À créer côté backend
        AxiosInstance.get("releve-heures/count-pending"), // À créer côté backend
        AxiosInstance.get("prolongation/count-pending"), // À créer côté backend
      ]);

      setNombreFacturesImpayees(facturesRes.data);
      setContratsASigner(contratsRes.data || 0);
      setRhASaisir(rhRes.data || 0);
      setProlongationsATraiter(prolongationsRes.data || 0);

      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
      // Valeurs par défaut en cas d'erreur
      setContratsASigner(0);
      setRhASaisir(0);
      setProlongationsATraiter(0);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="w-full min-h-screen ">
      <div className="w-full p-8">
        {/* Header Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">
                Bonjour {(() => {
                  const firstName = localStorage.getItem("firstname") || "";
                  return firstName.charAt(0).toUpperCase() + firstName.slice(1);
                })()} 👋
              </h1>
              <p className="text-lg text-slate-600">
                Voici un aperçu de votre activité aujourd'hui
              </p>
            </div>
          </div>
        </motion.div>

        {/* Métriques Principales */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Contrats à signer */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Contrats</p>
                  <p className="text-xs text-slate-500">À signer</p>
                </div>
              </div>
              <CheckCircle2 className="w-4 h-4 text-blue-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">
                {loading ? (
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                ) : (
                  contratsASigner
                )}
              </p>
              <p className="text-xs text-slate-500">En attente de signature</p>
            </div>
          </motion.div>

          {/* RH à saisir */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">RH</p>
                  <p className="text-xs text-slate-500">À saisir</p>
                </div>
              </div>
              <Clock className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">
                {loading ? (
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin"></div>
                ) : (
                  rhASaisir
                )}
              </p>
              <p className="text-xs text-slate-500">Relevés d'heures en attente</p>
            </div>
          </motion.div>

          {/* Prolongations à traiter */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Repeat className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Prolongations</p>
                  <p className="text-xs text-slate-500">À traiter</p>
                </div>
              </div>
              <Activity className="w-4 h-4 text-purple-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">
                {loading ? (
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-purple-500 rounded-full animate-spin"></div>
                ) : (
                  prolongationsATraiter
                )}
              </p>
              <p className="text-xs text-slate-500">Demandes en cours</p>
            </div>
          </motion.div>

          {/* Factures Impayées */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Factures</p>
                  <p className="text-xs text-slate-500">Impayées</p>
                </div>
              </div>
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">
                {loading ? (
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
                ) : (
                  nombreFacturesImpayees
                )}
              </p>
              <p className="text-xs text-slate-500">En attente de paiement</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Actions Rapides */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Section Gauche - Actions Principales */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-slate-600" />
              Actions Rapides
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Briefcase, label: "Contrats", href: "/Contrat", color: "bg-blue-50 hover:bg-blue-100 text-blue-700" },
                { icon: Clock, label: "Relevés RH", href: "/relevesdheures", color: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700" },
                { icon: Repeat, label: "Prolongations", href: "/Prolongations", color: "bg-purple-50 hover:bg-purple-100 text-purple-700" },
                { icon: FileText, label: "Factures", href: "/facture", color: "bg-amber-50 hover:bg-amber-100 text-amber-700" },
              ].map((action, index) => (
                <motion.button
                  key={index}
                  onClick={() => navigate(action.href)}
                  className={`p-4 rounded-lg transition-all duration-200 ${action.color} group`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <action.icon className="w-6 h-6 mb-2 mx-auto" />
                  <p className="text-sm font-medium text-center">{action.label}</p>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Section Droite - Activité Récente */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-slate-600" />
              Activité Récente
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Nouveau contrat en attente</p>
                  <p className="text-xs text-slate-500">Il y a 2 heures</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Relevé d'heures soumis</p>
                  <p className="text-xs text-slate-500">Il y a 4 heures</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <Repeat className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Demande de prolongation</p>
                  <p className="text-xs text-slate-500">Hier</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Facture en retard</p>
                  <p className="text-xs text-slate-500">Il y a 2 jours</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Informations Utiles */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {/* Prochaines Échéances */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-slate-600" />
              Prochaines Échéances
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border-l-4 border-blue-500 bg-blue-50 rounded-r-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">Contrat Mission ABC</p>
                  <p className="text-xs text-slate-500">Signature requise</p>
                </div>
                <span className="text-xs font-medium text-blue-600">Aujourd'hui</span>
              </div>
              <div className="flex items-center justify-between p-3 border-l-4 border-emerald-500 bg-emerald-50 rounded-r-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">Relevé d'heures S47</p>
                  <p className="text-xs text-slate-500">À soumettre</p>
                </div>
                <span className="text-xs font-medium text-emerald-600">Demain</span>
              </div>
              <div className="flex items-center justify-between p-3 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">Facture #2024-156</p>
                  <p className="text-xs text-slate-500">Échéance paiement</p>
                </div>
                <span className="text-xs font-medium text-amber-600">Dans 3 jours</span>
              </div>
            </div>
          </div>

          {/* Raccourcis Utiles */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center">
              <Home className="w-5 h-5 mr-2 text-slate-600" />
              Raccourcis Utiles
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Building, label: "Mon Profil", href: "/monCompte", color: "bg-slate-50 hover:bg-slate-100 text-slate-700" },
                { icon: Library, label: "Documents", href: "/bibliotheque", color: "bg-slate-50 hover:bg-slate-100 text-slate-700" },
                { icon: Bell, label: "Notifications", href: "/annonces", color: "bg-slate-50 hover:bg-slate-100 text-slate-700" },
                { icon: UserCheck, label: "Support", href: "#", color: "bg-slate-50 hover:bg-slate-100 text-slate-700" },
              ].map((item, index) => (
                <motion.button
                  key={index}
                  onClick={() => item.href !== "#" && navigate(item.href)}
                  className={`p-4 rounded-lg transition-all duration-200 ${item.color} group`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <item.icon className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs font-medium text-center">{item.label}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
